import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import { apiPalette } from '@/components/Api/apiTokens';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import ApiHero from '@/components/Api/ApiHero';
import ApiAuto from '@/components/Api/ApiAuto';
import ApiAgents from '@/components/Api/ApiAgents';
import ApiEndpoints from '@/components/Api/ApiEndpoints';
import ApiPricing from '@/components/Api/ApiPricing';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import { MODEL_TOTAL, PROVIDER_TOTAL } from '@/constants/apiContent';

function ApiPageInner() {
  const insets = useSafeAreaInsets();
  return (
    <>
      <ApiHero topInset={insets.top} />
      <ApiAuto />
      <ApiAgents />
      <ApiEndpoints />
      <ApiPricing />
    </>
  );
}

export default function ApiPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: apiPalette(isDark).page,
  }), [isDark]);

  return (
    <View style={containerStyle} dataSet={{ apiPage: isDark ? 'dark' : 'light' }}>
      <MetaHead
        title={`Gab AI API — ${MODEL_TOTAL} Models, One API`}
        description={`Swap to ${MODEL_TOTAL} of the latest frontier and open-source models with one OpenAI-compatible API, or use Auto Router to automatically select the best model for your task. ${MODEL_TOTAL} models, ${PROVIDER_TOTAL} providers, one key.`}
        canonical="/api"
        keywords={`AI API router, Auto Router, OpenRouter alternative, ${MODEL_TOTAL} AI models, unified AI API, OpenAI compatible API, model=auto, automatic model routing, GPT API, Claude API, Gemini API, Gab AI API`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Gab AI API Router',
          description: `Swap to ${MODEL_TOTAL} frontier and open-source models with one API, or use Auto Router to select the best model for the task. ${MODEL_TOTAL} models from ${PROVIDER_TOTAL} providers.`,
          url: 'https://gab.ai/api',
          mainEntity: {
            '@type': 'SoftwareApplication',
            name: 'Gab AI API Router',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Any',
            programmingLanguage: ['Python', 'JavaScript', 'Any OpenAI-compatible client'],
            featureList: [
              `Swap ${MODEL_TOTAL} frontier and open-source models with one API`,
              'Auto Router selects the best model for the task',
              'Built for agents — Cursor, Claude Code, Codex, OpenCode, and compatible CLIs',
              'OpenAI-compatible API',
              'Chat, image, video, audio, and embeddings',
            ],
          },
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <ApiPageInner />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
